import React, { useState } from 'react';
import axios from 'axios';
import { FiAlertTriangle, FiArrowRight, FiSliders } from 'react-icons/fi';
import { API_URL } from '../../../utils/authUtils';

/**
 * TriagePanel - Handles SUBMITTED and TRIAGE status states.
 *
 * Allows staff to:
 * - View request criteria
 * - Adjust priority score
 * - Transition to IN_REVIEW, DECLINED, or WITHDRAWN
 */

const TriagePanel = ({ requestDetail, requestId, onRequestUpdated, authHeaders }) => {
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priorityValue, setPriorityValue] = useState(
    ((requestDetail?.priority_score || 0.5) * 100).toFixed(0)
  );
  const [prioritySaving, setPrioritySaving] = useState(false);

  // Decline dialog
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const getErrorMessage = (err, defaultMsg) => {
    if (err.response?.data) {
      const detail = err.response.data.detail;
      if (typeof detail === 'string') return detail;
      if (detail?.message) return detail.message;
    }
    return defaultMsg;
  };

  const transitionRequest = async (targetStatus, reasonCode = null, notes = null) => {
    setTransitionLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/transition`,
        {
          target_status: targetStatus,
          reason_code: reasonCode,
          notes: notes,
          row_version: requestDetail.row_version,
        },
        authHeaders()
      );
      onRequestUpdated();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Conflict: This request was modified by another user. Refreshing...');
        onRequestUpdated();
      } else {
        setError(getErrorMessage(err, `Failed to transition to ${targetStatus}`));
      }
    } finally {
      setTransitionLoading(false);
    }
  };

  const savePriority = async () => {
    setPrioritySaving(true);
    setError(null);
    try {
      await axios.patch(
        `${API_URL}/api/staff/brokerage/requests/${requestId}`,
        {
          priority_score: parseInt(priorityValue, 10) / 100,
          row_version: requestDetail.row_version,
        },
        authHeaders()
      );
      onRequestUpdated();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Conflict: Modified by another user. Refreshing...');
        onRequestUpdated();
      } else {
        setError(getErrorMessage(err, 'Failed to update priority'));
      }
    } finally {
      setPrioritySaving(false);
    }
  };

  const handleDecline = () => {
    if (!declineReason.trim()) {
      setError('Please provide a reason for declining');
      return;
    }
    transitionRequest('DECLINED', 'staff_decision', declineReason);
    setShowDeclineDialog(false);
    setDeclineReason('');
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Triage Review</h3>
        <p className="text-xs text-gray-500">
          Review the request criteria and decide whether to proceed to candidate matching.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Request Criteria */}
      {requestDetail.request_payload && Object.keys(requestDetail.request_payload).length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase mb-3">Request Criteria</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {Object.entries(requestDetail.request_payload).map(([key, value]) => {
              let displayValue;
              if (value === null || value === undefined) {
                displayValue = <span className="text-gray-400 italic">Not specified</span>;
              } else if (Array.isArray(value)) {
                displayValue = value.length > 0 ? value.join(', ') : <span className="text-gray-400 italic">None</span>;
              } else if (typeof value === 'object') {
                const entries = Object.entries(value);
                if (entries.length === 0) {
                  displayValue = <span className="text-gray-400 italic">None</span>;
                } else {
                  displayValue = (
                    <div className="mt-1 pl-2 border-l-2 border-gray-200 space-y-1">
                      {entries.map(([k, v]) => (
                        <div key={k} className="text-xs">
                          <span className="text-gray-500">{k}: </span>
                          <span className="text-gray-700">{Array.isArray(v) ? v.join(', ') : String(v ?? '')}</span>
                        </div>
                      ))}
                    </div>
                  );
                }
              } else if (typeof value === 'boolean') {
                displayValue = value ? 'Yes' : 'No';
              } else {
                displayValue = String(value);
              }

              return (
                <div key={key} className="bg-gray-50 rounded p-2">
                  <span className="text-gray-500 text-xs uppercase">{key.replace(/_/g, ' ')}</span>
                  <div className="font-medium text-gray-800 mt-0.5">{displayValue}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Priority Score Adjustment */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <FiSliders className="w-4 h-4 text-gray-500" />
          <p className="text-xs font-medium text-gray-500 uppercase">Priority Score</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max="100"
            value={priorityValue}
            onChange={(e) => setPriorityValue(e.target.value)}
            className="flex-1 h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-indigo-600"
          />
          <span className="text-sm font-medium text-gray-700 w-12 text-right">{priorityValue}%</span>
          <button
            onClick={savePriority}
            disabled={prioritySaving || parseInt(priorityValue, 10) === Math.round((requestDetail.priority_score || 0.5) * 100)}
            className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {prioritySaving ? 'Saving...' : 'Update'}
          </button>
        </div>
      </div>

      {/* Requester & Target Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Requester</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold">
                {(requestDetail.requester_name || 'U')[0].toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-medium text-gray-800">{requestDetail.requester_name || 'Unknown'}</p>
              <p className="text-xs text-gray-500 font-mono">{requestDetail.requester_person_id?.substring(0, 8)}...</p>
            </div>
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Target</p>
          {requestDetail.target_person_id ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-semibold">
                  {(requestDetail.target_name || 'T')[0].toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-medium text-gray-800">{requestDetail.target_name || 'Selected'}</p>
                <p className="text-xs text-gray-500 font-mono">{requestDetail.target_person_id?.substring(0, 8)}...</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">No target selected yet</p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="border border-gray-200 rounded-lg p-4">
        <p className="text-xs font-medium text-gray-500 uppercase mb-3">Actions</p>
        <div className="flex flex-wrap gap-2">
          {requestDetail.valid_transitions?.includes('TRIAGE') && (
            <button
              onClick={() => transitionRequest('TRIAGE')}
              disabled={transitionLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
            >
              <FiArrowRight className="w-3.5 h-3.5" /> Move to Triage
            </button>
          )}
          {requestDetail.valid_transitions?.includes('IN_REVIEW') && (
            <button
              onClick={() => transitionRequest('IN_REVIEW')}
              disabled={transitionLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
            >
              <FiArrowRight className="w-3.5 h-3.5" /> Proceed to Review
            </button>
          )}
          {requestDetail.valid_transitions?.includes('DECLINED') && (
            <button
              onClick={() => setShowDeclineDialog(true)}
              disabled={transitionLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 flex items-center gap-1"
            >
              <FiAlertTriangle className="w-3.5 h-3.5" /> Decline
            </button>
          )}
          {requestDetail.valid_transitions?.includes('WITHDRAWN') && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to withdraw this request?')) {
                  transitionRequest('WITHDRAWN');
                }
              }}
              disabled={transitionLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              Withdraw
            </button>
          )}
        </div>
      </div>

      {/* Decline Dialog */}
      {showDeclineDialog && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800 mb-2">Decline Request</p>
          <textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            rows={3}
            className="w-full text-sm border border-red-300 rounded-lg p-2 focus:ring-1 focus:ring-red-500 focus:border-red-500 mb-2"
            placeholder="Reason for declining this request..."
          />
          <div className="flex gap-2">
            <button
              onClick={handleDecline}
              disabled={transitionLoading}
              className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {transitionLoading ? 'Declining...' : 'Confirm Decline'}
            </button>
            <button
              onClick={() => { setShowDeclineDialog(false); setDeclineReason(''); }}
              className="px-4 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TriagePanel;
